import { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Phone } from "lucide-react";
import { ConversationView, type ConversationViewHandle } from "@/components/messaging/ConversationView";
import { wsClient } from "@/lib/websocket";
import { useSiteConfig } from "@/hooks/useSiteConfig";

function getVisitorId(): string {
  const key = "mll_visitor_id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = `visitor_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, id);
  }
  return id;
}

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [visitorId] = useState(getVisitorId);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [agentOnline, setAgentOnline] = useState(false);
  const [visitorName, setVisitorName] = useState(localStorage.getItem("mll_visitor_name") || "");
  const [visitorPhone, setVisitorPhone] = useState(localStorage.getItem("mll_visitor_phone") || "");
  const [identityCaptured, setIdentityCaptured] = useState(!!localStorage.getItem("mll_visitor_name"));
  const conversationRef = useRef<ConversationViewHandle>(null);
  const config = useSiteConfig();

  // Listen for agent availability globally
  useEffect(() => {
    const unsub = wsClient.on("agent_availability", (data: any) => {
      setAgentOnline(data.available);
    });
    return unsub;
  }, []);

  // Listen for "open-chat" custom event from CTA buttons (optionally with draft message)
  const [draftMessage, setDraftMessage] = useState("");
  useEffect(() => {
    const handler = (e: Event) => {
      setIsOpen(true);
      const detail = (e as CustomEvent).detail;
      if (detail?.draft) {
        setDraftMessage(detail.draft);
      }
    };
    window.addEventListener("open-chat", handler);
    return () => window.removeEventListener("open-chat", handler);
  }, []);

  // Early return if internal chat is disabled
  if (!config.internalChatEnabled) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[100]" data-testid="chat-widget">
      {isOpen && (
          <div
            className="fixed bottom-20 right-3 left-3 sm:left-auto sm:right-6 sm:bottom-24 w-[calc(100vw-3rem)] sm:w-[320px] h-[70vh] sm:h-[450px] bg-[#141414] rounded-2xl shadow-2xl border border-gray-800 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
            data-testid="chat-window"
          >
            {/* Custom header with agent status */}
            <div className="p-3 bg-gradient-to-r from-[#D4AF37] to-[#B48F1B] text-black flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${agentOnline ? "bg-emerald-600" : "bg-black/40"} animate-pulse`} />
                <span className="font-bold uppercase tracking-widest text-[9px]">
                  {agentOnline ? "Agent Online" : "Leave a Message"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {agentOnline && (
                  <button
                    type="button"
                    onClick={() => conversationRef.current?.startCall()}
                    className="p-1 hover:bg-black/10 rounded-full transition-colors"
                    title="Voice call"
                  >
                    <Phone size={14} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="hover:rotate-90 transition-transform"
                  title="Close chat"
                  data-testid="button-close-chat"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Identity capture or conversation view */}
            {!identityCaptured ? (
              <div className="flex-1 flex flex-col p-4 justify-center">
                <div className="text-center mb-4">
                  <p className="text-white text-sm font-medium mb-1">Welcome</p>
                  <p className="text-gray-400 text-[10px]">Please enter your details to start chatting</p>
                </div>
                <div className="space-y-3">
                  <input
                    value={visitorName}
                    onChange={e => setVisitorName(e.target.value)}
                    placeholder="Your name"
                    className="w-full bg-[#1A1A1A] border border-gray-800 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-[#D4AF37]"
                  />
                  <input
                    value={visitorPhone}
                    onChange={e => setVisitorPhone(e.target.value)}
                    placeholder="Phone number (optional)"
                    className="w-full bg-[#1A1A1A] border border-gray-800 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-[#D4AF37]"
                  />
                  <button
                    type="button"
                    disabled={!visitorName.trim()}
                    onClick={() => {
                      localStorage.setItem("mll_visitor_name", visitorName.trim());
                      if (visitorPhone.trim()) localStorage.setItem("mll_visitor_phone", visitorPhone.trim());
                      setIdentityCaptured(true);
                    }}
                    className="w-full py-2.5 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest rounded-lg disabled:opacity-50"
                  >
                    Start Chat
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col overflow-hidden">
                <ConversationView
                  ref={conversationRef}
                  role="customer"
                  userId={visitorId}
                  conversationId={conversationId}
                  onConversationCreated={setConversationId}
                  draftMessage={draftMessage}
                  onDraftConsumed={() => setDraftMessage("")}
                  showHeader={false}
                />
              </div>
            )}
          </div>
        )}

      {/* Floating chat button with online indicator */}
      <div className="relative">
        {agentOnline && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-[#0A0A0A] z-10 animate-pulse" />
        )}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-14 h-14 bg-gradient-to-br from-[#F1D279] via-[#D4AF37] to-[#B48F1B] text-black rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform ring-4 ring-black/50"
          title="Open chat"
          data-testid="button-open-chat"
        >
          <MessageSquare size={24} />
        </button>
      </div>
    </div>
  );
}
